/**
 * Local Studio Store
 *
 * Dumb persistence helper for localStorage operations.
 * No business logic - just get/set/clear operations.
 * Business logic (like one-story limit) is enforced in the repository.
 */

import type {
  LocalStudioStateV1,
  LocalActiveStory,
  LocalMigrationState,
} from '@/types/studio'
import { LOCAL_STORAGE_KEYS, LOCAL_STUDIO_VERSION } from '@/types/studio'

/**
 * Reads the local studio state for a child
 */
export function getLocalStudioState(childId: string): LocalStudioStateV1 | null {
  try {
    const key = LOCAL_STORAGE_KEYS.getStudioKey(childId)
    const stored = localStorage.getItem(key)
    if (!stored) return null

    const parsed = JSON.parse(stored) as LocalStudioStateV1

    // Version check for future migrations
    if (parsed.version !== LOCAL_STUDIO_VERSION) {
      console.warn('Unknown local studio version:', parsed.version)
      return null
    }

    return parsed
  } catch (err) {
    console.error('Error reading local studio state:', err)
    return null
  }
}

/**
 * Saves the local studio state for a child
 */
export function saveLocalStudioState(state: LocalStudioStateV1): boolean {
  try {
    const key = LOCAL_STORAGE_KEYS.getStudioKey(state.childId)
    localStorage.setItem(key, JSON.stringify(state))
    return true
  } catch (err) {
    console.error('Error saving local studio state:', err)
    return false
  }
}

/**
 * Gets or initializes the local studio state for a child
 */
export function getOrCreateLocalStudioState(childId: string): LocalStudioStateV1 {
  const existing = getLocalStudioState(childId)
  if (existing) return existing

  const initial: LocalStudioStateV1 = {
    version: 1,
    childId,
    activeStory: null,
  }

  saveLocalStudioState(initial)
  return initial
}

/**
 * Gets the active story for a child (or null if none)
 */
export function getLocalActiveStory(childId: string): LocalActiveStory | null {
  const state = getLocalStudioState(childId)
  return state?.activeStory ?? null
}

/**
 * Sets the active story for a child
 */
export function setLocalActiveStory(childId: string, story: LocalActiveStory | null): boolean {
  const state = getOrCreateLocalStudioState(childId)
  state.activeStory = story
  return saveLocalStudioState(state)
}

/**
 * Updates the migration state for a child
 */
export function updateMigrationState(childId: string, migration: LocalMigrationState): boolean {
  const state = getOrCreateLocalStudioState(childId)
  state.migration = migration
  return saveLocalStudioState(state)
}

/**
 * Gets the migration state for a child
 */
export function getMigrationState(childId: string): LocalMigrationState | undefined {
  const state = getLocalStudioState(childId)
  return state?.migration
}

/**
 * Clears the local studio state completely
 */
export function clearLocalStudioState(childId: string): void {
  const key = LOCAL_STORAGE_KEYS.getStudioKey(childId)
  localStorage.removeItem(key)
}

/**
 * Checks if migration has been completed for this child
 */
export function isMigrationCompleted(childId: string): boolean {
  const migration = getMigrationState(childId)
  return migration?.status === 'completed'
}

/**
 * Marks migration as completed with the cloud story ID
 */
export function markMigrationCompleted(childId: string, cloudStoryId: string): boolean {
  return updateMigrationState(childId, {
    status: 'completed',
    completedAt: new Date().toISOString(),
    cloudStoryId,
  })
}

/**
 * Marks migration as started
 */
export function markMigrationStarted(childId: string): boolean {
  return updateMigrationState(childId, {
    status: 'started',
    startedAt: new Date().toISOString(),
  })
}

/**
 * Marks migration as failed
 */
export function markMigrationFailed(childId: string, error: string): boolean {
  const existing = getMigrationState(childId)
  return updateMigrationState(childId, {
    status: 'failed',
    startedAt: existing?.startedAt,
    error,
  })
}
